import SEO from "../../components/SEO";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UserIcon,
  LockClosedIcon,
  AtSymbolIcon,
  PhoneIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  UsersIcon,
  BuildingOffice2Icon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import logo from "../../assets/logo.svg";
import bannerSchool from "../../assets/banner-school.png";
import { SignInUser } from "../../services/ApiUsers";

/* ── Types ── */
type Tab = "estudante" | "encarregado" | "instituicao";

const tabs: { id: Tab; label: string; Icon: React.ElementType }[] = [
  { id: "estudante",   label: "Estudante",    Icon: AcademicCapIcon },
  { id: "encarregado", label: "Encarregado",  Icon: UsersIcon },
  { id: "instituicao", label: "Instituição",  Icon: BuildingOffice2Icon },
];

/* ── Field helper ── */
function Field({
  Icon, label, type = "text", placeholder, value, onChange, error,
  rightSlot,
}: {
  Icon: React.ElementType; label: string; type?: string;
  placeholder: string; value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string; rightSlot?: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-dark/70 text-xs font-semibold mb-1.5">{label}</label>
      <div className={`flex items-center gap-3 border-2 rounded-xl px-4 py-3 bg-white transition-colors ${
        error ? "border-danger" : "border-border focus-within:border-blue"
      }`}>
        <Icon className="w-4 h-4 text-muted shrink-0" />
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="flex-1 text-sm text-dark placeholder:text-muted/60 outline-none bg-transparent"
        />
        {rightSlot}
      </div>
      {error && <p className="text-danger text-[11px] mt-1 ml-1 font-medium">{error}</p>}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════ */
export default function UserLogin() {
  const navigate = useNavigate();

  const [tab, setTab]           = useState<Tab>("estudante");
  const [showPass, setShowPass] = useState(false);

  /* Institution form state */
  const [form, setForm]           = useState({ email: "", senha: "" });
  const [errors, setErrors]       = useState<{ email?: string; senha?: string }>({});
  const [apiError, setApiError]   = useState<string | null>(null);
  const [loading, setLoading]     = useState(false);

  const onChangeField = (field: "email" | "senha") => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: undefined }));
    setApiError(null);
  };

  const handleInstLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const erros: typeof errors = {};
    if (!form.email) erros.email = "Insira o seu e-mail";
    if (!form.senha) erros.senha = "Insira a sua palavra-passe";
    if (Object.keys(erros).length) { setErrors(erros); return; }

    setLoading(true);
    setApiError(null);
    try {
      const data = await SignInUser(form) as {
        usuario?: { autenticado: { nivel: number } };
        token?: string;
        message?: string;
      };
      if (data.usuario && data.token) {
        localStorage.setItem("app-token", data.token);
        localStorage.setItem("usuario", JSON.stringify(data.usuario));
        const nivel = data.usuario.autenticado.nivel;
        if (nivel === 100) navigate("/admin");
        else if (nivel === 1) navigate("/instituicao");
        else if (nivel === 2) navigate("/encarregado");
        else navigate("/error_acesso");
      } else if (data.message) {
        setApiError(data.message);
      }
    } catch {
      setApiError("Erro de ligação. Verifique a sua internet e tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex max-md:flex-col">
      <SEO
        title="Entrar na Conta"
        description="Aceda à sua conta PIA como Estudante, Encarregado ou Instituição. Plataforma de pagamentos escolares em Angola."
        path="/access-user"
        noIndex={false}
      />

      {/* ── Left photo panel ── */}
      <div
        className="relative flex-1 flex flex-col justify-between px-14 py-12 overflow-hidden max-md:hidden"
        style={{
          backgroundImage: `url(${bannerSchool})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,rgba(0,22,39,0.93) 0%,rgba(0,40,73,0.80) 50%,rgba(5,105,163,0.65) 100%)" }} />
        <div className="absolute bottom-0 left-0 w-full h-56 pointer-events-none" style={{ background: "linear-gradient(to top,rgba(0,22,39,0.9),transparent)" }} />
        <div className="relative flex-1 flex flex-col justify-center py-12 max-w-sm">
          <span className="text-blue-light text-xs font-semibold uppercase tracking-widest mb-4">
            Bem-vindo de volta
          </span>
          <h1 className="text-4xl font-extrabold text-white leading-[1.15] mb-5">
            A sua instituição{" "}
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg,#2babe3,#7dd3fc)" }}>
              na palma da mão
            </span>
          </h1>
          <p className="text-white/50 text-sm leading-relaxed mb-10">
            Aceda à sua conta para gerir pagamentos, enviar convocatórias
            e acompanhar a sua instituição em tempo real.
          </p>
          <div className="flex flex-col gap-3">
            {[
              { Icon: ShieldCheckIcon, t: "Acesso 100% seguro e encriptado" },
              { Icon: AcademicCapIcon, t: "Estudantes, Encarregados e Instituições" },
            ].map(({ Icon, t }) => (
              <div key={t} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-blue-light" />
                </div>
                <span className="text-white/60 text-sm">{t}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 inline-flex items-center gap-3 bg-white/8 backdrop-blur-sm border border-white/12 rounded-2xl px-5 py-4 w-fit">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/70 text-sm">Plataforma activa em Angola</span>
          </div>
        </div>

        <p className="relative text-white/25 text-xs">© 2024 PIA · Todos os direitos reservados</p>
      </div>

      <div className="w-px bg-border max-md:hidden" />

      {/* ── Right form panel ── */}
      <div className="flex-1 bg-off-white flex flex-col justify-center px-14 py-16 max-md:px-8 max-md:py-20">

        <div className="hidden max-md:block mb-10">
          <Link to="/"><img src={logo} alt="PIA" className="h-8" /></Link>
        </div>

        <p className="text-muted text-xs font-semibold uppercase tracking-widest mb-2">Acesso à plataforma</p>
        <h2 className="text-2xl font-extrabold text-dark mb-1">Entrar na sua conta</h2>
        <p className="text-muted text-sm mb-8">Seleccione o tipo de utilizador e introduza os seus dados</p>

        {/* Tab switcher — 3 tabs */}
        <div className="flex bg-white border border-border rounded-2xl p-1.5 mb-8 gap-1">
          {tabs.map(({ id, label, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => { setTab(id); setApiError(null); setErrors({}); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all duration-200 ${
                  active ? "text-white shadow-md" : "text-muted hover:text-dark"
                }`}
                style={active ? { background: "linear-gradient(135deg,#2babe3,#016fb9)" } : {}}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        {/* ── Estudante ── */}
        {tab === "estudante" && (
          <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
            <Field Icon={UserIcon}      label="Número de estudante" placeholder="Ex: 2024-EST-0001" />
            <div>
              <label className="block text-dark/70 text-xs font-semibold mb-1.5">Palavra-passe</label>
              <div className="flex items-center gap-3 border-2 border-border rounded-xl px-4 py-3 bg-white focus-within:border-blue transition-colors">
                <LockClosedIcon className="w-4 h-4 text-muted shrink-0" />
                <input type={showPass ? "text" : "password"} placeholder="A sua palavra-passe"
                  className="flex-1 text-sm text-dark placeholder:text-muted/60 outline-none bg-transparent" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="text-muted hover:text-dark transition-colors" tabIndex={-1}>
                  {showPass ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <FooterLinks />
            <SubmitBtn label="Entrar como Estudante" loading={false} />
          </form>
        )}

        {/* ── Encarregado ── */}
        {tab === "encarregado" && (
          <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
            <Field Icon={PhoneIcon}     label="Telefone ou E-mail" placeholder="Ex: +244 9XX XXX XXX" />
            <div>
              <label className="block text-dark/70 text-xs font-semibold mb-1.5">Palavra-passe</label>
              <div className="flex items-center gap-3 border-2 border-border rounded-xl px-4 py-3 bg-white focus-within:border-blue transition-colors">
                <LockClosedIcon className="w-4 h-4 text-muted shrink-0" />
                <input type={showPass ? "text" : "password"} placeholder="A sua palavra-passe"
                  className="flex-1 text-sm text-dark placeholder:text-muted/60 outline-none bg-transparent" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="text-muted hover:text-dark transition-colors" tabIndex={-1}>
                  {showPass ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <FooterLinks />
            <SubmitBtn label="Entrar como Encarregado" loading={false} />
          </form>
        )}

        {/* ── Instituição (API real) ── */}
        {tab === "instituicao" && (
          <form className="flex flex-col gap-4" onSubmit={handleInstLogin}>
            <Field
              Icon={AtSymbolIcon} label="E-mail institucional"
              placeholder="admin@instituicao.ao" type="email"
              value={form.email} onChange={onChangeField("email")} error={errors.email}
            />
            <Field
              Icon={LockClosedIcon} label="Palavra-passe"
              placeholder="A sua palavra-passe" type={showPass ? "text" : "password"}
              value={form.senha} onChange={onChangeField("senha")} error={errors.senha}
              rightSlot={
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="text-muted hover:text-dark transition-colors" tabIndex={-1}>
                  {showPass ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              }
            />

            {apiError && (
              <div className="bg-danger/8 border border-danger/20 rounded-xl px-4 py-3 text-danger text-xs font-medium">
                {apiError}
              </div>
            )}

            <FooterLinks />
            <SubmitBtn label={loading ? "A entrar…" : "Entrar como Instituição"} loading={loading} />
          </form>
        )}

        {/* Register CTA */}
        <div className="mt-5 text-center bg-white border-2 border-border rounded-2xl p-4">
          <p className="text-muted text-xs mb-2">Ainda não tem conta?</p>
          <Link to="/register" className="text-blue font-bold text-sm hover:text-blue-light transition-colors">
            Criar conta gratuitamente →
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Shared sub-components ── */
function FooterLinks() {
  return (
    <div className="flex justify-between items-center">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="accent-blue w-3.5 h-3.5" />
        <span className="text-muted text-xs">Lembrar-me</span>
      </label>
      <Link to="/forgetpassword" className="text-blue text-xs font-semibold hover:text-blue-light transition-colors">
        Esqueceu a palavra-passe?
      </Link>
    </div>
  );
}

function SubmitBtn({ label, loading }: { label: string; loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-white font-bold text-sm mt-1 transition-all hover:opacity-90 hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
      style={{ background: "linear-gradient(90deg,#2babe3,#016fb9)" }}
    >
      {label}
      {!loading && <ArrowRightIcon className="w-4 h-4" />}
    </button>
  );
}
